/* ============================================================
   NEXZ Luxury Animations
   Controlled confidence — restraint over spectacle.
   ============================================================ */
(function () {
  'use strict';

  // ===== Architecture layer ================================
  window.NEXZ = {
    isReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    isDesignMode: !!(window.Shopify && window.Shopify.designMode),
    isTouch: window.matchMedia('(pointer: coarse)').matches,
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
    loaderDone: false,
    libsReady: false,
  };

  const NEXZ = window.NEXZ;

  // Hard exits
  if (NEXZ.isDesignMode) return;
  if (NEXZ.isReducedMotion) {
    document.documentElement.classList.add('nexz-reduced-motion');
    const loader = document.getElementById('nexz-loader');
    if (loader) loader.remove();
    return;
  }

  // ===== Script loader =====================================
  // Prefer self-hosted libraries (served from the Shopify CDN via window.NEXZ_LIBS,
  // injected in theme.liquid). Fall back to public CDNs only if unavailable.
  const CDN_FALLBACK = {
    lenis: 'https://cdn.jsdelivr.net/npm/@studio-freight/lenis@1.0.42/bundled/lenis.min.js',
    gsap: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js',
    scrollTrigger: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js',
  };
  const HOSTED = (typeof window !== 'undefined' && window.NEXZ_LIBS) || {};
  const CDN = {
    lenis: HOSTED.lenis || CDN_FALLBACK.lenis,
    gsap: HOSTED.gsap || CDN_FALLBACK.gsap,
    scrollTrigger: HOSTED.scrollTrigger || CDN_FALLBACK.scrollTrigger,
  };

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === '1') return resolve();
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
        return;
      }
      const s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.crossOrigin = 'anonymous';
      s.addEventListener('load', () => {
        s.dataset.loaded = '1';
        resolve();
      });
      s.addEventListener('error', reject);
      document.head.appendChild(s);
    });
  }

  async function loadLibs() {
    try {
      // GSAP first (Lenis can wait), then ScrollTrigger needs gsap on window
      await Promise.all([loadScript(CDN.gsap), loadScript(CDN.lenis)]);
      await loadScript(CDN.scrollTrigger);
      if (window.gsap && window.ScrollTrigger) {
        window.gsap.registerPlugin(window.ScrollTrigger);
      }
      NEXZ.libsReady = true;
      return true;
    } catch (e) {
      console.warn('[NEXZ] CDN libs failed — graceful fallback', e);
      document.documentElement.classList.add('nexz-reduced-motion');
      return false;
    }
  }

  // ===== 1. PAGE INTRO LOADER ==============================
  function runLoader() {
    const loader = document.getElementById('nexz-loader');
    if (!loader) {
      NEXZ.loaderDone = true;
      document.documentElement.classList.add('nexz-loader-finished');
      return Promise.resolve();
    }

    let alreadyShown = false;
    try {
      alreadyShown = !!sessionStorage.getItem('nexz-loader-shown');
    } catch (_) { /* sessionStorage might be blocked */ }

    if (alreadyShown) {
      loader.classList.add('is-done');
      document.documentElement.classList.add('nexz-loader-finished');
      NEXZ.loaderDone = true;
      window.dispatchEvent(new CustomEvent('nexzLoaderDone'));
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Force reflow then trigger reveal next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          loader.classList.add('is-revealing');
        });
      });

      // 0.45s reveal + 0.15s hold = 600ms then fade. Kept short so the intro
      // never holds the first paint of product images for long.
      setTimeout(() => {
        loader.classList.add('is-done');
        document.documentElement.classList.add('nexz-loader-finished');
        try { sessionStorage.setItem('nexz-loader-shown', '1'); } catch (_) {}
        NEXZ.loaderDone = true;
        window.dispatchEvent(new CustomEvent('nexzLoaderDone'));
        // 0.4s fade
        setTimeout(resolve, 400);
      }, 600);
    });
  }

  // ===== 3. HERO TEXT REVEAL ===============================
  function splitText(el) {
    const text = (el.textContent || '').trim();
    if (!text) return false;
    el.setAttribute('aria-label', text);
    el.innerHTML = '';
    const words = text.split(/\s+/);
    words.forEach((word, i) => {
      const wordSpan = document.createElement('span');
      wordSpan.className = 'nexz-word';
      wordSpan.setAttribute('aria-hidden', 'true');
      for (const ch of word) {
        const wrap = document.createElement('span');
        wrap.className = 'nexz-char-wrap';
        const charSpan = document.createElement('span');
        charSpan.className = 'nexz-char';
        charSpan.textContent = ch;
        wrap.appendChild(charSpan);
        wordSpan.appendChild(wrap);
      }
      el.appendChild(wordSpan);
      if (i < words.length - 1) {
        const space = document.createElement('span');
        space.className = 'nexz-word-space';
        space.setAttribute('aria-hidden', 'true');
        el.appendChild(space);
      }
    });
    el.dataset.nexzSplit = '1';
    return true;
  }

  function revealHeroText() {
    const heroWrap = document.querySelector('.hero__content-wrapper');
    if (!heroWrap) return;

    const headings = heroWrap.querySelectorAll('h1, h2, h3');
    headings.forEach((el) => {
      if (!el.dataset.nexzSplit) splitText(el);
    });

    if (!window.gsap) return;

    const chars = heroWrap.querySelectorAll('.nexz-char');
    if (chars.length) {
      window.gsap.fromTo(
        chars,
        { yPercent: 110 },
        {
          yPercent: 0,
          duration: 0.9,
          ease: 'power4.out',
          stagger: { each: 0.03, from: 'start' },
        }
      );
    }

    // Subtitle paragraphs + buttons fade up
    const subtitles = heroWrap.querySelectorAll('p, .button, [class*="button"]');
    if (subtitles.length) {
      window.gsap.fromTo(
        subtitles,
        { opacity: 0, y: 18 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.08,
          delay: 0.35,
        }
      );
    }
  }

  function attachHeroResize() {
    let lastWidth = window.innerWidth;
    let t;
    window.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (Math.abs(window.innerWidth - lastWidth) < 50) return;
        lastWidth = window.innerWidth;
        const heroWrap = document.querySelector('.hero__content-wrapper');
        if (!heroWrap) return;
        heroWrap.querySelectorAll('[data-nexz-split]').forEach((el) => {
          const label = el.getAttribute('aria-label');
          if (!label) return;
          el.textContent = label;
          delete el.dataset.nexzSplit;
          splitText(el);
          if (window.gsap) {
            window.gsap.set(el.querySelectorAll('.nexz-char'), { yPercent: 0 });
          }
        });
      }, 250);
    });
  }

  // ===== 2. LENIS SMOOTH SCROLL ============================
  let lenis = null;

  function initLenis() {
    if (NEXZ.isIOS) return; // Safari's native momentum is already premium
    if (!window.Lenis || !window.gsap) return;

    lenis = new window.Lenis({
      // Higher lerp = lighter smoothing, snappier and less laggy/janky on
      // mid-range and mobile devices than the previous heavy 0.085.
      lerp: 0.13,
      smoothWheel: true,
      smoothTouch: false,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });

    document.documentElement.classList.add('lenis', 'lenis-smooth');

    window.gsap.ticker.add((time) => lenis.raf(time * 1000));
    window.gsap.ticker.lagSmoothing(0);

    // Stop Lenis when Shopify locks scroll (drawers, modals, search)
    new MutationObserver(() => {
      if (document.documentElement.hasAttribute('scroll-lock')) lenis.stop();
      else lenis.start();
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['scroll-lock'],
    });
  }

  // ===== 4. PRODUCT CARD STAGGER ===========================
  function dynamicStagger(i) {
    // First row (i 0-3) tighter, rows after that slightly slower
    if (i < 4) return i * 0.08;
    return 4 * 0.08 + (i - 4) * 0.12;
  }

  function animateCards(cards) {
    if (!window.gsap || !cards.length) return;
    cards.forEach((c) => {
      c.style.willChange = 'transform, opacity';
    });
    window.gsap.fromTo(
      cards,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 0.75,
        ease: 'power3.out',
        stagger: dynamicStagger,
        onComplete: () => {
          cards.forEach((c) => {
            c.style.willChange = 'auto';
            c.dataset.nexzAnimated = '1';
          });
        },
      }
    );
  }

  function initProductCards() {
    if (!window.gsap || !window.ScrollTrigger) return;

    const cardSelector = 'product-card, .product-card';
    const allCards = Array.from(document.querySelectorAll(cardSelector))
      .filter((c) => !c.dataset.nexzAnimated);

    // Only animate cards that are below the fold. Above-the-fold cards stay
    // visible so their images paint immediately (better LCP, no blank wait).
    const foldLine = window.innerHeight * 0.9;
    const initialCards = [];
    allCards.forEach((c) => {
      if (c.getBoundingClientRect().top > foldLine) {
        initialCards.push(c);
      } else {
        c.dataset.nexzAnimated = '1';
      }
    });

    if (initialCards.length) {
      window.gsap.set(initialCards, { opacity: 0, y: 40 });

      window.ScrollTrigger.batch(initialCards, {
        once: true,
        start: 'top 92%',
        onEnter: (batch) => animateCards(batch),
      });
    }

    // Watch product grids for dynamically loaded cards (infinite scroll)
    const grids = document.querySelectorAll(
      '.product-grid, .resource-list, [class*="product-list"]'
    );
    grids.forEach((grid) => {
      const obs = new MutationObserver((mutations) => {
        const newCards = [];
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.matches && node.matches(cardSelector)) {
              if (!node.dataset.nexzAnimated) newCards.push(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll(cardSelector).forEach((c) => {
                if (!c.dataset.nexzAnimated) newCards.push(c);
              });
            }
          });
        });
        if (!newCards.length) return;
        window.gsap.set(newCards, { opacity: 0, y: 40 });
        window.ScrollTrigger.batch(newCards, {
          once: true,
          start: 'top 92%',
          onEnter: (batch) => animateCards(batch),
        });
      });
      obs.observe(grid, { childList: true, subtree: true });
    });
  }

  // ===== 5. SCROLL REVEAL ==================================
  function initScrollReveal() {
    if (!window.gsap || !window.ScrollTrigger) return;

    // Only hide/animate blocks below the fold; on-screen blocks stay visible
    // so content paints immediately instead of waiting for the scroll trigger.
    const foldLine = window.innerHeight * 0.9;
    const reveals = Array.from(document.querySelectorAll('[data-scroll-reveal]'))
      .filter((el) => el.getBoundingClientRect().top > foldLine);
    if (reveals.length) {
      window.gsap.set(reveals, { opacity: 0, y: 32 });
      window.ScrollTrigger.batch(reveals, {
        once: true,
        start: 'top 90%',
        onEnter: (batch) => {
          window.gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power3.out',
            stagger: 0.1,
          });
        },
      });
    }

    // Progress bar width animation
    document.querySelectorAll('[data-bar-width]').forEach((fill) => {
      const targetW = fill.dataset.barWidth;
      window.ScrollTrigger.create({
        trigger: fill,
        start: 'top 90%',
        once: true,
        onEnter: () => {
          fill.style.width = targetW + '%';
        },
      });
    });
  }

  // ===== 6. STATS COUNTER ==================================
  function initCounters() {
    if (!window.gsap || !window.ScrollTrigger) return;
    document.querySelectorAll('[data-counter]').forEach((el) => {
      const target = parseFloat(el.dataset.counter);
      const suffix = el.dataset.counterSuffix || '';
      window.ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: () => {
          const obj = { val: 0 };
          window.gsap.to(obj, {
            val: target,
            duration: 1.4,
            ease: 'power2.out',
            onUpdate: () => {
              el.textContent = Math.round(obj.val) + suffix;
            },
            onComplete: () => {
              el.textContent = target + suffix;
            },
          });
        },
      });
    });
  }

  // ===== 7. CUSTOM CURSOR ==================================
  function initCursor() {
    if (NEXZ.isTouch) return;
    const dot = document.getElementById('nexz-cursor-dot');
    const ring = document.getElementById('nexz-cursor-ring');
    if (!dot || !ring) return;

    let mx = -100;
    let my = -100;
    let rx = -100;
    let ry = -100;
    const lerp = 0.18;
    let activated = false;

    const setTransform = (el, x, y, scale) => {
      el.style.transform =
        `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)` +
        (scale ? ` scale(${scale})` : '');
    };

    document.addEventListener(
      'pointermove',
      (e) => {
        if (e.pointerType !== 'mouse') return;
        mx = e.clientX;
        my = e.clientY;
        setTransform(dot, mx, my);
        if (!activated) {
          activated = true;
          document.body.classList.add('nexz-cursor-active');
        }
      },
      { passive: true }
    );

    let ringScale = 1;
    let targetScale = 1;
    function raf() {
      rx += (mx - rx) * lerp;
      ry += (my - ry) * lerp;
      ringScale += (targetScale - ringScale) * 0.18;
      setTransform(ring, rx, ry, ringScale);
      requestAnimationFrame(raf);
    }
    raf();

    const hoverSelector = 'a, button, [role="button"], .button, summary, [data-cursor-hover]';
    document.addEventListener('mouseover', (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(hoverSelector)) {
        targetScale = 1.8;
        dot.style.opacity = '0.5';
      }
    });
    document.addEventListener('mouseout', (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(hoverSelector)) {
        targetScale = 1;
        dot.style.opacity = '';
      }
    });

    // Exclusion zones — restore native cursor over inputs/video
    const excludeSelector = 'input, textarea, select, video, [type="number"], .quantity-input';
    document.addEventListener('mouseover', (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(excludeSelector)) {
        document.body.classList.add('nexz-cursor-hidden');
      }
    });
    document.addEventListener('mouseout', (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(excludeSelector)) {
        document.body.classList.remove('nexz-cursor-hidden');
      }
    });

    document.addEventListener('mouseleave', () => {
      document.body.classList.add('nexz-cursor-hidden');
    });
    document.addEventListener('mouseenter', () => {
      document.body.classList.remove('nexz-cursor-hidden');
    });
  }

  // ===== Orchestration =====================================
  // Safety net: product cards and [data-scroll-reveal] blocks start hidden
  // (opacity: 0) via CSS to prevent FOUC. They are only revealed once GSAP +
  // ScrollTrigger are ready. If the libraries stall or fail to load, that hiding
  // must never become permanent — otherwise images simply never appear. Dropping
  // the FOUC hiding is done by adding `nexz-reduced-motion`, which deactivates the
  // `.nexz-js:not(.nexz-reduced-motion)` CSS rules and restores full opacity.
  let revealed = false;
  function revealAllContent() {
    if (revealed) return;
    revealed = true;
    document.documentElement.classList.add('nexz-reduced-motion');
    const loader = document.getElementById('nexz-loader');
    if (loader) loader.classList.add('is-done');
    document.documentElement.classList.add('nexz-loader-finished');
    NEXZ.loaderDone = true;
  }

  // Hard watchdog — never keep content hidden longer than this, regardless of
  // network conditions. Comfortably exceeds the intro loader (~1.5s) so a normal
  // first visit still gets the animations, but a slow/stalled CDN can't hold the
  // images hostage.
  const WATCHDOG_MS = 2500;

  async function init() {
    const loaderPromise = runLoader();
    const watchdog = setTimeout(revealAllContent, WATCHDOG_MS);

    const libsLoaded = await loadLibs();

    // Libraries unavailable (or didn't expose gsap): reveal everything now so
    // images are never stuck behind the FOUC opacity:0 rule.
    if (!libsLoaded || !window.gsap) {
      clearTimeout(watchdog);
      revealAllContent();
      initCursor();
      return;
    }

    await loaderPromise;
    clearTimeout(watchdog);

    // The watchdog may have already fired (slow libs) and switched the page into
    // reduced-motion mode — in that case skip the GSAP-driven reveals to avoid
    // re-hiding content that is already visible.
    if (!revealed) {
      revealHeroText();
      attachHeroResize();
      initLenis();
      initProductCards();
      initScrollReveal();
      initCounters();
    }

    // Cursor is independent of GSAP — runs even if CDN fails
    initCursor();
  }

  // ===== View Transitions hook =============================
  window.addEventListener('pagereveal', () => {
    // Loader sessionStorage flag will keep it hidden; ensure done state.
    const loader = document.getElementById('nexz-loader');
    if (loader && !loader.classList.contains('is-done')) {
      loader.classList.add('is-done');
    }
    NEXZ.loaderDone = true;
    // Re-run hero reveal on new page
    if (window.gsap) revealHeroText();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
